// Combobox — JS-driven rich select.
//
// Replaces the native <select> with a custom dropdown that supports search,
// multi-select with removable chips, and an optional "create new" affordance.
// The native element stays in the DOM (visually hidden) so its value is
// submitted with the surrounding <form> exactly like before.
//
// HTML contract:
//
//   Every <select class="bf-select"> automatically becomes a combobox at
//   DOMContentLoaded. To opt out (and use the native browser picker) add
//   data-bf-combobox-skip to the element.
//
//     <select class="bf-select">…</select>                       ← combobox (default)
//     <select class="bf-select" multiple data-bf-creatable>…</select>
//     <select class="bf-select" data-bf-combobox-skip>…</select> ← native picker
//
// The data-bf-combobox attribute is still honored for selects that don't
// carry .bf-select (so addons can opt in explicitly).
//
// Optional:
//   data-bf-combobox-placeholder="Pick a country"
//   data-bf-combobox-empty="No matches"
//   data-bf-combobox-no-search    — disables the typeahead input (single mode only)
//   data-bf-creatable             — Enter key creates a new tag from the typed text

import { $$, on, type Cleanup } from './util';

interface OptionRecord {
  value: string;
  label: string;
  disabled: boolean;
  group?: string;
  custom?: boolean;
}

interface Settings {
  placeholder: string;
  emptyText: string;
  creatable: boolean;
  searchable: boolean;
}

const ICON_X = '<svg viewBox="0 0 16 16" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 3l10 10M13 3L3 13"/></svg>';
const ICON_CHECK = '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 8.5 6.5 12 13 5"/></svg>';
const ICON_CHEVRON = '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6l4 4 4-4"/></svg>';

class Combobox {
  private root!: HTMLDivElement;
  private field!: HTMLDivElement;
  private input!: HTMLInputElement;
  private valueEl!: HTMLSpanElement;
  private panel!: HTMLDivElement;
  private chipHost!: HTMLDivElement;
  private settings: Settings;
  private options: OptionRecord[] = [];
  private filtered: OptionRecord[] = [];
  private highlighted = -1;
  private selected = new Set<string>();
  private isMultiple: boolean;
  private destroyed = false;
  private cleanups: Cleanup[] = [];

  constructor(private select: HTMLSelectElement) {
    this.isMultiple = select.multiple;
    this.settings = {
      placeholder: select.dataset.bfComboboxPlaceholder ?? select.getAttribute('placeholder') ?? (this.isMultiple ? 'Select…' : 'Choose one'),
      emptyText: select.dataset.bfComboboxEmpty ?? 'No results',
      creatable: select.hasAttribute('data-bf-creatable'),
      searchable: !select.hasAttribute('data-bf-combobox-no-search'),
    };
    this.options = this.readOptions();
    for (const o of this.options) if (o.value && this.isOptionInitiallySelected(o)) this.selected.add(o.value);
    this.build();
    this.bind();
    this.renderChips();
    this.renderField();
    this.renderPanel();
  }

  private isOptionInitiallySelected(o: OptionRecord): boolean {
    return Array.from(this.select.options).some(opt => opt.value === o.value && opt.selected);
  }

  private readOptions(): OptionRecord[] {
    const out: OptionRecord[] = [];
    const walk = (parent: Element, group?: string) => {
      for (const child of Array.from(parent.children)) {
        if (child.tagName === 'OPTGROUP') {
          walk(child, (child as HTMLOptGroupElement).label);
        } else if (child.tagName === 'OPTION') {
          const opt = child as HTMLOptionElement;
          // Skip the empty placeholder option that's commonly used as a hint.
          if (!opt.value && !opt.textContent?.trim()) continue;
          const rec: OptionRecord = {
            value: opt.value,
            label: (opt.textContent ?? opt.value).trim(),
            disabled: opt.disabled,
          };
          if (group !== undefined) rec.group = group;
          out.push(rec);
        }
      }
    };
    walk(this.select);
    return out;
  }

  private build(): void {
    const root = document.createElement('div');
    root.className = 'bf-combobox';
    if (this.isMultiple) root.classList.add('bf-combobox--multi');
    else root.classList.add('bf-combobox--single');
    if (this.select.disabled) root.setAttribute('aria-disabled', 'true');
    if (this.select.getAttribute('aria-invalid') === 'true') root.setAttribute('aria-invalid', 'true');
    // Carry over size modifiers from the original .bf-select if any.
    for (const cls of Array.from(this.select.classList)) {
      if (cls.startsWith('bf-select--')) root.classList.add(cls.replace('bf-select--', 'bf-combobox--'));
    }

    const field = document.createElement('div');
    field.className = 'bf-combobox__field';
    field.tabIndex = this.select.disabled ? -1 : 0;
    field.setAttribute('role', 'combobox');
    field.setAttribute('aria-haspopup', 'listbox');
    field.setAttribute('aria-expanded', 'false');

    const chipHost = document.createElement('div');
    chipHost.className = 'bf-combobox__chips';
    chipHost.style.display = 'contents';

    const valueEl = document.createElement('span');
    valueEl.className = 'bf-combobox__value bf-combobox__value--placeholder';
    valueEl.textContent = this.settings.placeholder;

    const input = document.createElement('input');
    input.className = 'bf-combobox__input';
    input.type = 'text';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.setAttribute('aria-autocomplete', 'list');
    if (!this.settings.searchable && !this.isMultiple) {
      // Read-only typing for non-searchable single selects: still capture
      // keyboard but don't accept text.
      input.readOnly = true;
    }

    const caret = document.createElement('i');
    caret.className = 'bf-combobox__caret';
    caret.setAttribute('aria-hidden', 'true');
    caret.innerHTML = ICON_CHEVRON;

    field.appendChild(chipHost);
    if (!this.isMultiple) field.appendChild(valueEl);
    field.appendChild(input);
    field.appendChild(caret);

    const panel = document.createElement('div');
    panel.className = 'bf-combobox__panel';
    panel.setAttribute('role', 'listbox');
    panel.id = `bf-combobox-${Math.random().toString(36).slice(2, 8)}`;
    field.setAttribute('aria-controls', panel.id);
    if (this.isMultiple) field.setAttribute('aria-multiselectable', 'true');

    root.appendChild(field);
    root.appendChild(panel);

    // Hide the native select but keep it for form submission.
    this.select.classList.add('bf-combobox__native');
    this.select.tabIndex = -1;
    this.select.setAttribute('aria-hidden', 'true');
    this.select.parentElement!.insertBefore(root, this.select);
    root.appendChild(this.select);

    this.root = root;
    this.field = field;
    this.input = input;
    this.valueEl = valueEl;
    this.panel = panel;
    this.chipHost = chipHost;
  }

  private bind(): void {
    this.cleanups.push(on(this.field, 'mousedown', (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('.bf-combobox__chip__remove') || t.closest('.bf-combobox__clear')) return;
      if (t === this.input) return;
      e.preventDefault();
      this.input.focus();
      this.open();
    }));

    this.cleanups.push(on(this.field, 'keydown', (e) => this.onKeydown(e as KeyboardEvent)));
    this.cleanups.push(on(this.input, 'keydown', (e) => this.onKeydown(e as KeyboardEvent)));
    this.cleanups.push(on(this.input, 'input', () => this.filterAndRender()));
    this.cleanups.push(on(this.input, 'focus', () => this.open()));

    this.cleanups.push(on(this.panel, 'mousedown', (e) => {
      const opt = (e.target as HTMLElement).closest('.bf-combobox__option') as HTMLElement | null;
      if (!opt || opt.dataset.disabled === 'true') return;
      e.preventDefault();
      if (opt.classList.contains('bf-combobox__option--create')) {
        const newValue = this.input.value.trim();
        if (newValue) this.createOption(newValue);
      } else {
        this.toggle(opt.dataset.value!);
      }
    }));

    const docMouseDown = (e: Event) => {
      if (this.destroyed) return;
      if (!this.root.contains(e.target as Node)) this.close();
    };
    document.addEventListener('mousedown', docMouseDown);
    this.cleanups.push(() => document.removeEventListener('mousedown', docMouseDown));
  }

  private onKeydown(e: KeyboardEvent): void {
    const isOpen = this.root.dataset.bfOpen === 'true';
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) { this.open(); return; }
        this.move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) { this.open(); return; }
        this.move(-1);
        break;
      case 'Enter':
        if (isOpen) {
          e.preventDefault();
          const opt = this.filtered[this.highlighted];
          if (opt) this.toggle(opt.value);
          else if (this.settings.creatable && this.input.value.trim()) this.createOption(this.input.value.trim());
        }
        break;
      case 'Escape':
        if (isOpen) { e.preventDefault(); this.close(); }
        break;
      case 'Backspace':
        if (this.isMultiple && this.input.value === '' && this.selected.size > 0) {
          // Pop the last chip on backspace when input is empty.
          const last = Array.from(this.selected).pop()!;
          this.toggle(last);
        }
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  private move(delta: number): void {
    if (this.filtered.length === 0) return;
    let next = this.highlighted + delta;
    // Skip disabled options.
    let guard = this.filtered.length;
    while (guard-- > 0) {
      if (next < 0) next = this.filtered.length - 1;
      else if (next >= this.filtered.length) next = 0;
      if (!this.filtered[next]?.disabled) break;
      next += delta;
    }
    this.highlighted = next;
    this.renderHighlight();
    this.scrollHighlightedIntoView();
  }

  private filterAndRender(): void {
    this.applyFilter();
    this.renderPanel();
    if (this.root.dataset.bfOpen !== 'true') this.open();
  }

  private applyFilter(): void {
    const q = this.input.value.trim().toLowerCase();
    if (!q) {
      this.filtered = this.options.slice();
    } else {
      this.filtered = this.options.filter(o => o.label.toLowerCase().includes(q));
    }
    this.highlighted = this.filtered.findIndex(o => !o.disabled);
  }

  private toggle(value: string): void {
    if (this.isMultiple) {
      if (this.selected.has(value)) this.selected.delete(value);
      else this.selected.add(value);
      // Keep the dropdown open; clear the search input.
      this.input.value = '';
      this.applyFilter();
    } else {
      this.selected.clear();
      this.selected.add(value);
      this.input.value = '';
      this.close();
    }
    this.syncNative();
    this.renderChips();
    this.renderField();
    this.renderPanel();
  }

  private createOption(label: string): void {
    if (!this.options.some(o => o.label.toLowerCase() === label.toLowerCase())) {
      const rec: OptionRecord = { value: label, label, disabled: false, custom: true };
      this.options.push(rec);
      // Append to the native select so the value submits.
      const opt = document.createElement('option');
      opt.value = label;
      opt.textContent = label;
      this.select.appendChild(opt);
    }
    this.toggle(label);
  }

  private renderField(): void {
    if (this.isMultiple) {
      this.input.placeholder = this.selected.size === 0 ? this.settings.placeholder : '';
      return;
    }
    // Single mode: show the chosen label as static text; show input only when typing or open.
    const isOpen = this.root.dataset.bfOpen === 'true';
    const hasQuery = this.input.value !== '';
    const showInput = isOpen || hasQuery;
    this.valueEl.style.display = showInput ? 'none' : '';
    this.input.style.display = showInput ? '' : 'none';
    if (this.selected.size > 0) {
      const v = Array.from(this.selected)[0]!;
      const opt = this.options.find(o => o.value === v);
      this.valueEl.textContent = opt?.label ?? v;
      this.valueEl.classList.remove('bf-combobox__value--placeholder');
    } else {
      this.valueEl.textContent = this.settings.placeholder;
      this.valueEl.classList.add('bf-combobox__value--placeholder');
    }
    this.input.placeholder = this.selected.size === 0 ? this.settings.placeholder : '';
  }

  private renderChips(): void {
    if (!this.isMultiple) { this.chipHost.innerHTML = ''; return; }
    this.chipHost.innerHTML = '';
    for (const value of this.selected) {
      const opt = this.options.find(o => o.value === value);
      if (!opt) continue;
      const chip = document.createElement('span');
      chip.className = 'bf-combobox__chip';
      chip.dataset.value = opt.value;
      const label = document.createElement('span');
      label.className = 'bf-combobox__chip__label';
      label.textContent = opt.label;
      chip.appendChild(label);
      if (!opt.disabled) {
        const x = document.createElement('button');
        x.type = 'button';
        x.className = 'bf-combobox__chip__remove';
        x.setAttribute('aria-label', `Remove ${opt.label}`);
        x.innerHTML = ICON_X;
        x.addEventListener('mousedown', (e) => {
          e.preventDefault();
          this.toggle(opt.value);
        });
        chip.appendChild(x);
      } else {
        chip.dataset.disabled = 'true';
      }
      this.chipHost.appendChild(chip);
    }
  }

  private renderPanel(): void {
    this.panel.innerHTML = '';
    if (this.filtered.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'bf-combobox__empty';
      empty.textContent = this.settings.emptyText;
      this.panel.appendChild(empty);
      if (this.settings.creatable && this.input.value.trim()) this.appendCreateOption(this.input.value.trim());
      return;
    }
    let lastGroup: string | undefined;
    this.filtered.forEach((o, i) => {
      if (o.group && o.group !== lastGroup) {
        const g = document.createElement('div');
        g.className = 'bf-combobox__group';
        g.textContent = o.group;
        this.panel.appendChild(g);
        lastGroup = o.group;
      }
      const el = document.createElement('div');
      el.className = 'bf-combobox__option';
      el.setAttribute('role', 'option');
      el.dataset.value = o.value;
      if (o.disabled) el.dataset.disabled = 'true';
      if (this.selected.has(o.value)) el.setAttribute('aria-selected', 'true');
      if (i === this.highlighted) el.dataset.highlighted = 'true';

      const check = document.createElement('span');
      check.className = 'bf-combobox__option__check';
      check.innerHTML = ICON_CHECK;
      const label = document.createElement('span');
      label.className = 'bf-combobox__option__label';
      label.innerHTML = this.highlightMatch(o.label);
      el.appendChild(check);
      el.appendChild(label);
      this.panel.appendChild(el);
    });
    // Append "create new" option if creatable and the typed value is novel.
    if (this.settings.creatable && this.input.value.trim()) {
      const q = this.input.value.trim().toLowerCase();
      const exists = this.options.some(o => o.label.toLowerCase() === q);
      if (!exists) this.appendCreateOption(this.input.value.trim());
    }
  }

  private appendCreateOption(label: string): void {
    const el = document.createElement('div');
    el.className = 'bf-combobox__option bf-combobox__option--create';
    el.setAttribute('role', 'option');
    el.textContent = `Create "${label}"`;
    this.panel.appendChild(el);
  }

  private highlightMatch(label: string): string {
    const q = this.input.value.trim();
    if (!q) return this.escapeHtml(label);
    const i = label.toLowerCase().indexOf(q.toLowerCase());
    if (i === -1) return this.escapeHtml(label);
    return (
      this.escapeHtml(label.slice(0, i)) +
      `<span class="bf-combobox__option__match">${this.escapeHtml(label.slice(i, i + q.length))}</span>` +
      this.escapeHtml(label.slice(i + q.length))
    );
  }

  private escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
  }

  private renderHighlight(): void {
    const items = this.panel.querySelectorAll<HTMLElement>('.bf-combobox__option');
    items.forEach((el, i) => {
      if (i === this.highlighted) el.dataset.highlighted = 'true';
      else delete el.dataset.highlighted;
    });
  }

  private scrollHighlightedIntoView(): void {
    const items = this.panel.querySelectorAll<HTMLElement>('.bf-combobox__option');
    const el = items[this.highlighted];
    if (!el) return;
    const top = el.offsetTop;
    const bot = top + el.offsetHeight;
    if (top < this.panel.scrollTop) this.panel.scrollTop = top;
    else if (bot > this.panel.scrollTop + this.panel.clientHeight) this.panel.scrollTop = bot - this.panel.clientHeight;
  }

  private syncNative(): void {
    for (const opt of Array.from(this.select.options)) opt.selected = this.selected.has(opt.value);
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  open(): void {
    if (this.root.dataset.bfOpen === 'true') return;
    this.applyFilter();
    this.renderPanel();
    this.placePanel();
    this.root.dataset.bfOpen = 'true';
    this.field.setAttribute('aria-expanded', 'true');
    this.renderField();
  }

  close(): void {
    if (this.root.dataset.bfOpen !== 'true') return;
    delete this.root.dataset.bfOpen;
    this.field.setAttribute('aria-expanded', 'false');
    this.input.value = '';
    this.applyFilter();
    this.renderField();
  }

  // If the field is too close to the bottom of the viewport, flip the panel up.
  private placePanel(): void {
    const fieldRect = this.field.getBoundingClientRect();
    const room = window.innerHeight - fieldRect.bottom;
    if (room < 200 && fieldRect.top > room) this.root.dataset.bfFlip = 'up';
    else delete this.root.dataset.bfFlip;
  }

  destroy(): void {
    this.destroyed = true;
    for (const c of this.cleanups) c();
    this.cleanups = [];
    // Restore the native select.
    if (this.root.parentElement) {
      this.root.parentElement.insertBefore(this.select, this.root);
      this.root.remove();
    }
    this.select.classList.remove('bf-combobox__native');
    this.select.removeAttribute('aria-hidden');
    this.select.tabIndex = 0;
  }
}

const instances = new WeakMap<HTMLSelectElement, Combobox>();

export function attach(select: HTMLSelectElement): Cleanup {
  const existing = instances.get(select);
  if (existing) return () => existing.destroy();
  const cb = new Combobox(select);
  instances.set(select, cb);
  return () => { cb.destroy(); instances.delete(select); };
}

export function init(root: ParentNode = document): void {
  // Auto-upgrade every .bf-select that hasn't opted out, plus any explicit
  // [data-bf-combobox] selects (handy for selects that don't carry the .bf-select class).
  const selector = 'select.bf-select:not([data-bf-combobox-skip]):not([data-bf-combobox-attached]), select[data-bf-combobox]:not([data-bf-combobox-attached])';
  for (const sel of $$<HTMLSelectElement>(selector, root)) {
    sel.setAttribute('data-bf-combobox-attached', '');
    attach(sel);
  }
}

export function open(select: HTMLSelectElement): void { instances.get(select)?.open(); }
export function close(select: HTMLSelectElement): void { instances.get(select)?.close(); }
